import { SelectOptionType } from "@impero-com/common/components/form/fields/selectInput";
import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { isEqual } from "lodash-es";
import moment from "moment-timezone";

import {
    CreatableTestingControlBody,
    Month,
    NewTestingControlPlanning,
    NewTestingControlStep,
    StepAllocatedTime,
    TestingControl,
    TestingControlStepGroup,
    TestingControlReviewerAnswerKind,
    TestingControlReviewerFieldKind,
    UpdatableTestingControl,
    UpdatableTestingControlStep,
    ControlWithFieldsView,
    ControlWithoutFieldsGraphItemDto,
    ControlWithoutFieldsGraphItemDtoListDto as ControlWithoutFieldsListGraphDto,
    InitialAssignmentKind,
    UserGroupView,
} from "api/generated/model";
import { FieldGroup } from "components/fieldBuilder/models";
import { SelectMultipleValueType } from "components/form/fields/selectMultipleInput";
import { AllocatedTimeSchema } from "components/form/rhfFields/rhfAllocatedTimeInput";
import {
    microsToHours,
    hoursToMicros,
    MICROSECONDS_IN_AN_HOUR,
} from "org/control/view/utils/microsecondsToTime";
import { TagsQuerySelect } from "org/tags/queries/useTagsQuery";
import { fetchUserGroupQuery } from "org/user/queries/useUserGroupsQuery";
import { getIfChanged, diffToPresence } from "utils/diff";
import { generate } from "utils/guid";

import {
    TestingControlBulkCreateSchema,
    TestingControlCreateSchema,
    TestingControlEditSchema,
    TestingControlFormSchema,
    TestingControlTimezone,
} from "./testingControlSchema";

function dateToMonth(date: Date): Month {
    const monthName = moment(date).locale("en").format("MMMM");
    return monthName as Month;
}

function toSchemaAllocatedTime(
    allocatedTime: StepAllocatedTime
): AllocatedTimeSchema {
    return allocatedTime.microseconds === 0
        ? { value: allocatedTime.days, unit: "Days" as const }
        : {
              value: microsToHours(allocatedTime.microseconds),
              unit: "Hours" as const,
          };
}

function toApiAllocatedTime(
    allocatedTime: AllocatedTimeSchema
): StepAllocatedTime {
    let days = 0;
    let microseconds = 0;
    switch (allocatedTime.unit) {
        case "Days":
            days = allocatedTime.value;
            break;
        case "Hours":
            microseconds = allocatedTime.value * MICROSECONDS_IN_AN_HOUR;
            break;
        default:
            assertNever(allocatedTime);
    }
    return { days, microseconds };
}

export function filterActiveTags(
    tagIds: string[],
    tagsData: TagsQuerySelect
): string[] {
    return tagIds.filter((tag) => {
        const tagResource = tagsData.tags.find(
            (tagInData) => tagInData.id === tag
        );
        if (!tagResource) {
            // Tag not found in resource - assuming it may be deleted
            return false;
        }
        return tagResource.active;
    });
}

export function toTestingControlEditSchemaData(
    resource: TestingControl,
    tagsData: TagsQuerySelect
): TestingControlEditSchema {
    return {
        controlId: resource.controlToTestId,
        description: resource.description,
        title: resource.title,
        tagIds: filterActiveTags(resource.tags, tagsData),
        timezoneName: resource.scheduling
            .timezoneName as TestingControlTimezone,
        samplingTagIds: filterActiveTags(
            resource.samplingCriteria.samplingTags,
            tagsData
        ), // We trust server state is correct
        dueDate: new Date(
            `${resource.scheduling.dueDate}T${resource.scheduling.dueTime}`
        ),
        period: {
            start: new Date(
                resource.samplingCriteria.periodStartYear,
                moment()
                    .month(resource.samplingCriteria.periodStartMonth)
                    .month()
            ),
            end: new Date(
                resource.samplingCriteria.periodEndYear,
                moment().month(resource.samplingCriteria.periodEndMonth).month()
            ),
        },
        responsible: {
            userId: resource.stepGroup.responsible.assignment!.userId, // We trust server state is correct
            allocatedTime: {
                unit: "Days" as const,
                value: resource.stepGroup.responsible.allocatedTime.days,
            },
        },
        notificationTime: moment(
            resource.scheduling.notificationTime,
            "HH:mm"
        ).toDate(),
        reviewers: {
            reviewer: {
                userId: resource.stepGroup.reviewer.assignment?.userId,
                allocatedTime: toSchemaAllocatedTime(
                    resource.stepGroup.reviewer.allocatedTime
                ),
            },
            secondReviewer: {
                userId: resource.stepGroup.secondReviewer.assignment?.userId,
                allocatedTime: toSchemaAllocatedTime(
                    resource.stepGroup.secondReviewer.allocatedTime
                ),
            },
        },
        remindersBefore: resource.scheduling.reminderDays
            .filter((day) => day < 0)
            .map((day) => Math.abs(day)),
        remindersAfter: resource.scheduling.reminderDays.filter(
            (day) => day > 0
        ),
        randomSampleCount: resource.randomSampleCount,
    };
}

function toApiAssignee(
    reviewer: TestingControlFormSchema["reviewers"]["reviewer"]
): UpdatableTestingControlStep | null {
    return reviewer
        ? {
              allocatedTime: toApiAllocatedTime(reviewer.allocatedTime),
              assignee: {
                  type: "User",
                  userId: reviewer.userId!,
              },
          }
        : null;
}

function resourceToApiAssignee(
    assigneeStep: Pick<
        TestingControlStepGroup["reviewer"],
        "assignment" | "allocatedTime"
    >
): UpdatableTestingControlStep | null {
    return assigneeStep.assignment
        ? {
              allocatedTime: assigneeStep.allocatedTime,
              assignee: assigneeStep.assignment,
          }
        : null;
}

type PartialObjectOrNull<T> = [T] extends [null]
    ? null
    : { [K in keyof T]: T[K] | undefined };

function deepDiff<T>(source: T, update: T): PartialObjectOrNull<T> | undefined {
    if (!isEqual(source, update)) {
        if (update === null) {
            return null as PartialObjectOrNull<T>;
        }
        const patch = {} as any;
        for (const key in update) {
            if (Object.hasOwn(update, key)) {
                patch[key] = getIfChanged(
                    (source as typeof update)?.[key],
                    update[key]
                );
            }
        }
        return patch;
    } else {
        return undefined;
    }
}

export function toUpdatableTestingControl(
    form: TestingControlEditSchema,
    resource: TestingControl
): UpdatableTestingControl {
    return {
        description: getIfChanged(resource.description, form.description),
        title: getIfChanged(resource.title, form.title),
        tags: diffToPresence(new Set(resource.tags), new Set(form.tagIds)),
        samplingTags: diffToPresence(
            new Set(resource.samplingCriteria.samplingTags),
            new Set(form.samplingTagIds)
        ),
        scheduling: deepDiff(resource.scheduling, {
            timezoneName: form.timezoneName,
            dueDate: moment(form.dueDate).format("YYYY-MM-DD"),
            dueTime: moment(form.dueDate).format("HH:mm"),
            notificationTime: moment(form.notificationTime).format("HH:mm"),
            reminderDays: [
                ...(form.remindersBefore?.map((daysBefore) => -daysBefore) ??
                    []),
                ...(form.remindersAfter ?? []),
            ],
        }),
        responsible: deepDiff(
            resourceToApiAssignee(resource.stepGroup.responsible),
            toApiAssignee(form.responsible)
        ) as any,
        firstReviewer: deepDiff(
            resourceToApiAssignee(resource.stepGroup.reviewer),
            toApiAssignee(form.reviewers.reviewer)
        ),
        secondReviewer: deepDiff(
            resourceToApiAssignee(resource.stepGroup.secondReviewer),
            toApiAssignee(form.reviewers.secondReviewer)
        ),
        samplingPeriodStart: getIfChanged(
            {
                month: resource.samplingCriteria.periodStartMonth,
                year: resource.samplingCriteria.periodStartYear,
            },
            {
                month: dateToMonth(form.period.start),
                year: moment(form.period.start).year(),
            }
        ),
        samplingPeriodEnd: getIfChanged(
            {
                month: resource.samplingCriteria.periodEndMonth,
                year: resource.samplingCriteria.periodEndYear,
            },
            {
                month: dateToMonth(form.period.end),
                year: moment(form.period.end).year(),
            }
        ),
        randomSampleCount: getIfChanged(
            resource.randomSampleCount,
            form.randomSampleCount
        ),
    };
}

function buildReviewerTasks(
    t: TFunction
): [
    FieldGroup<
        TestingControlReviewerFieldKind,
        TestingControlReviewerAnswerKind
    >,
    FieldGroup<
        TestingControlReviewerFieldKind,
        TestingControlReviewerAnswerKind
    >,
] {
    return [
        {
            id: generate(),
            fields: [
                {
                    id: generate(),
                    title: t(
                        "ControlTesting_TestingControl_ReviewerTaskCommentTitle"
                    ),
                    instructions: "",
                    fieldType: { type: "TextArea" },
                    mandatory: false,
                    condition: null,
                    blobIds: [],
                },
                {
                    id: generate(),
                    title: t(
                        "ControlTesting_TestingControl_ReviewerTaskFileUploadTitle"
                    ),
                    instructions: "",
                    fieldType: { type: "FileUpload" },
                    mandatory: false,
                    condition: null,
                    blobIds: [],
                },
            ],
        },
        {
            id: generate(),
            fields: [
                {
                    id: generate(),
                    title: t(
                        "ControlTesting_TestingControl_ReviewerTaskCommentTitle"
                    ),
                    instructions: "",
                    fieldType: { type: "TextArea" },
                    mandatory: false,
                    condition: null,
                    blobIds: [],
                },
                {
                    id: generate(),
                    title: t(
                        "ControlTesting_TestingControl_ReviewerTaskFileUploadTitle"
                    ),
                    instructions: "",
                    fieldType: { type: "FileUpload" },
                    mandatory: false,
                    condition: null,
                    blobIds: [],
                },
            ],
        },
    ] as const;
}

function buildPlanning(
    form: TestingControlCreateSchema | TestingControlBulkCreateSchema
): NewTestingControlPlanning {
    return {
        dueDate: moment(form.dueDate).format("YYYY-MM-DD"),
        dueTime: moment(form.dueDate).format("HH:mm"),
        timezoneName: form.timezoneName,
        reminderDays: [
            ...(form.remindersBefore?.map((days) => -days) ?? []),
            ...(form.remindersAfter ?? []),
        ],
        notificationTime: moment(form.notificationTime).format("HH:mm"),
        responsible: {
            assignee: {
                type: "User",
                userId: form.responsible.userId,
            },
            allocatedTime: toStepAllocatedTime(form.responsible.allocatedTime),
        },
        reviewers: toReviewers([
            form.reviewers.reviewer,
            form.reviewers.secondReviewer,
        ]),
    };
}

export function toCreatableTestingControlBody(
    data: TestingControlCreateSchema,
    testingProgramId: number,
    t: TFunction
): CreatableTestingControlBody {
    if (!data.controlId) {
        throw new Error(
            "Expected controlId to be defined - this should be caught by the schema"
        );
    }
    return {
        controlToTest: data.controlId,
        title: data.title,
        description: data.description,
        tagIds: data.tagIds,
        testingProgramId,
        planning: buildPlanning(data),
        samplingCriteria: {
            periodStartDate: {
                month: dateToMonth(data.period.start),
                year: moment(data.period.start).year(),
            },
            periodEndDate: {
                month: dateToMonth(data.period.end),
                year: moment(data.period.end).year(),
            },
            samplingTagIds: data.samplingTagIds,
        },
        reviewerTasks: buildReviewerTasks(t),
        randomSampleCount: data.randomSampleCount,
    };
}

export function getAllTagsFromControl(
    control: ControlWithoutFieldsGraphItemDto,
    userGroups: UserGroupView[]
): string[] {
    type Assignment =
        | { type: "Group" | "Pool"; userGroupId: number }
        | { type: "User"; userId: string };

    const controlAssignments = [
        control.stepGroup.initialStep?.assignments ?? [],
        ...(control.stepGroup.subsequentSteps.map((x) => x.assignments) ?? []),
    ];
    const groupIds: number[] = [];
    controlAssignments.forEach((stepAssignment) => {
        Object.values(stepAssignment).forEach((assignment: Assignment) => {
            if (assignment.type === "Group") {
                groupIds.push(assignment.userGroupId);
            }
        });
    });
    const tagIds = userGroups
        ?.filter((userGroup) => groupIds.includes(userGroup.id))
        .flatMap((userGroup) =>
            userGroup.userGroupUsers.flatMap((user) => user.userTagIds)
        );
    return [
        ...new Set([
            ...Object.values(control.assignmentSettings.tagIds).flat(),
            ...control.tagIds,
            ...tagIds,
        ]),
    ];
}

export function getSelectedControls(
    controls: ControlWithoutFieldsListGraphDto,
    selectedControlIds: string[]
): ControlWithoutFieldsGraphItemDto[] {
    return controls.content
        .filter((control) => selectedControlIds.includes(control.resource.id))
        .map((control) => control.resource);
}

/**
 * Extracts the control tag IDs that match the sampling tag IDs, including
 *  - The control's tags
 *  - The control's assignments' tags if they match the sampling tag ID
 *  - The user group's tags if the responsible are assigned individually
 */
function extractControlTagIds(
    controlTagIds: Set<string>,
    initialStepAssignments: Record<string, InitialAssignmentKind>,
    assignmentsTagIds: Record<string, string[]>,
    userGroupUserTags: Record<string, Set<string>>,
    samplingTagIds: Set<string>
) {
    const selectedTagIds = Object.entries(initialStepAssignments).reduce(
        (acc, [assignmentGroupId, assignmentKind]) => {
            let assignmentTagIds = new Set(
                assignmentsTagIds[assignmentGroupId]
            );

            switch (assignmentKind.type) {
                case "Group":
                    // In Individual assignment type (called "Group" in the codebase),
                    // the resulting activities also have the user in user group's tags.
                    // Therefore, we should include them in the match & selection.
                    assignmentTagIds = assignmentTagIds.union(
                        userGroupUserTags[assignmentKind.userGroupId]
                    );
                    break;
                case "Pool":
                case "User":
                    // Nothing to do
                    break;
                default:
                    assertNever(assignmentKind);
            }
            const matchingTagIds = controlTagIds.union(assignmentTagIds);
            return !matchingTagIds.isDisjointFrom(samplingTagIds)
                ? acc.union(assignmentTagIds)
                : acc;
        },
        // Start with the control's tags
        controlTagIds
    );

    return [...selectedTagIds];
}

// Specialized version of extractControlTagIds for bulk creation types
export function extractControlTagIdsForBulk(
    control: ControlWithoutFieldsGraphItemDto,
    userGroupUserTags: Record<string, Set<string>>,
    samplingTagId: string
) {
    return extractControlTagIds(
        new Set(control.tagIds),
        control.stepGroup.initialStep.assignments,
        control.assignmentSettings.tagIds,
        userGroupUserTags,
        new Set([samplingTagId])
    );
}

// Specialized version of extractControlTagIds for single creation/edit types
export function extractControlTagIdsForSingle(
    control: ControlWithFieldsView,
    userGroupUserTags: Record<string, Set<string>>,
    samplingTagIds: Set<string>
) {
    return extractControlTagIds(
        new Set(control.tagIds),
        control.stepGroup.initialStep.assignment,
        control.assignmentSettings.tagIds,
        userGroupUserTags,
        samplingTagIds
    );
}

export async function toBulkCreatableTestingControlBody(
    data: TestingControlBulkCreateSchema,
    testingProgramId: number,
    t: TFunction,
    controls: ControlWithoutFieldsListGraphDto
): Promise<CreatableTestingControlBody[]> {
    const userGroupTagData = await fetchUserGroupQuery();
    if (userGroupTagData.data.type !== "WithUsers") {
        throw new Error("Expected WithUsers response type");
    }
    const userGroupTags = userGroupTagData.data.data.content.flatMap(
        (dto) => dto.resource
    );
    const userGroupUserTags = Object.fromEntries(
        userGroupTagData.data.data.content.map(({ resource }) => [
            resource.id,
            new Set(resource.userGroupUsers.flatMap((user) => user.userTagIds)),
        ])
    );

    const bodies: CreatableTestingControlBody[] = getSelectedControls(
        controls,
        data.selectedControlIds
    ).reduce((acc: CreatableTestingControlBody[], control) => {
        const allTags = getAllTagsFromControl(control, userGroupTags ?? []);

        const newBodies = data.samplingTagIds
            .filter((samplingTagId) => allTags.includes(samplingTagId))
            .map((samplingTagId) => ({
                controlToTest: control.id,
                title: t("TestingControlView_Title_Autofill", {
                    title: control.title,
                }),
                description: "",
                tagIds: filterActiveTags(
                    extractControlTagIdsForBulk(
                        control,
                        userGroupUserTags,
                        samplingTagId
                    ),
                    {
                        tags: controls.linkedResources.tags.map(
                            (tagRes) => tagRes.resource
                        ),
                        tagCategories: controls.linkedResources.tagCategories,
                    }
                ),
                samplingCriteria: {
                    samplingTagIds: [samplingTagId],
                    periodStartDate: {
                        month: dateToMonth(data.period.start),
                        year: moment(data.period.start).year(),
                    },
                    periodEndDate: {
                        month: dateToMonth(data.period.end),
                        year: moment(data.period.end).year(),
                    },
                },
                planning: buildPlanning(data),
                testingProgramId,
                reviewerTasks: buildReviewerTasks(t),
                randomSampleCount: data.randomSampleCount,
            }));

        return [...acc, ...newBodies];
    }, []);

    return bodies;
}

function toReviewers(
    reviewers: TestingControlFormSchema["reviewers"]["reviewer"][]
): Array<NewTestingControlStep | null> {
    return reviewers.map((reviewer) => {
        if (reviewer) {
            return {
                assignee: {
                    type: "User",
                    userId: reviewer.userId!, // Schema transform ensures userId is present, if reviewer is present
                },
                allocatedTime: toStepAllocatedTime(reviewer.allocatedTime),
            };
        } else {
            return null;
        }
    });
}

function toStepAllocatedTime(
    allocatedTime: AllocatedTimeSchema
): StepAllocatedTime {
    switch (allocatedTime.unit) {
        case "Days": {
            return {
                days: allocatedTime.value,
                microseconds: 0,
            };
        }
        case "Hours": {
            return {
                days: 0,
                microseconds: hoursToMicros(allocatedTime.value),
            };
        }
        default: {
            assertNever(allocatedTime);
        }
    }
}

export function formatBulkCreatedTestsToNumber(
    control: ControlWithoutFieldsGraphItemDto,
    samplingTagIds: string[],
    userGroups: UserGroupView[]
) {
    const controlAndAssignmentTagIds = getAllTagsFromControl(
        control,
        userGroups
    );
    return controlAndAssignmentTagIds.reduce(
        (acc, aItem) => acc + (samplingTagIds.includes(aItem) ? 1 : 0),
        0
    );
}

export function generateRemindersOptions(
    t: TFunction
): SelectOptionType<number> {
    return Array.from(new Array(365), (_v, idx) => {
        return {
            value: idx + 1,
            label: `${t("TestingControlView_Reminders_Day", {
                count: idx + 1,
            })}`,
        };
    });
}

export function sortRemindersDescending(
    reminders: SelectMultipleValueType[]
): SelectMultipleValueType[] {
    return reminders.toSorted((a, b) => {
        // If both are numbers
        if (typeof a === "number" && typeof b === "number") {
            return b - a;
        }
        // If both are strings
        if (typeof a === "string" && typeof b === "string") {
            return b.localeCompare(a);
        }
        // Mixed types
        return String(b).localeCompare(String(a));
    });
}
